import { useState } from 'react';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, UserPlus, Shield, User } from "lucide-react";
import { listUsers, createUser, assignRoles } from "@/services/userManagementApi";

export default function UserRoleManager() {
  const [email, setEmail] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const queryClient = useQueryClient();

  const { data: users, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const result = await listUsers();
      return result;
    },
  });

  const handleInviteUser = async () => {
    if (!email) {
      toast.error("Please enter an email address");
      return;
    }
    setIsInviting(true);
    try {
      await createUser({ email, username: email } as any);
      toast.success(`Invitation sent to ${email}`);
      setEmail("");
      queryClient.invalidateQueries({ queryKey: ['users'] });
    } catch (error: any) {
      toast.error(error?.message || "Failed to invite user");
    } finally {
      setIsInviting(false);
    }
  };

  const handleSetAdmin = async (userEmail: string) => {
    try {
      await assignRoles({ email: userEmail, roleIds: ['admin'] });
      toast.success("Admin role assigned");
      queryClient.invalidateQueries({ queryKey: ['users'] });
    } catch (error: any) {
      toast.error(error?.message || "Failed to assign admin role");
    }
  };

  const handleSetTester = async (userEmail: string) => {
    try {
      await assignRoles({ email: userEmail, roleIds: ['tester'] });
      toast.success("Tester role assigned");
      queryClient.invalidateQueries({ queryKey: ['users'] });
    } catch (error: any) {
      toast.error(error?.message || "Failed to assign tester role");
    }
  };

  if (error) {
    return (
      <Card>
        <CardHeader><CardTitle>User Management</CardTitle></CardHeader>
        <CardContent>
          <p className="text-destructive">Error loading users: {(error as any).message}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>User Management</CardTitle>
        <CardDescription>Invite new users and manage their roles</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4 mb-6">
          <Input placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className="max-w-sm" />
          <Button onClick={handleInviteUser} disabled={isInviting}>
            {isInviting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Inviting...</>) : (<><UserPlus className="mr-2 h-4 w-4" />Invite User</>)}
          </Button>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users && users.length > 0 ? (
                users.map((user) => (
                  <TableRow key={user.id || user.email}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {user.roles && user.roles.length > 0 ? (
                          user.roles.map((role: any) => (
                            <Badge key={typeof role === 'string' ? role : role.id} variant="outline">
                              {typeof role === 'string' ? role : role.name || role.role}
                            </Badge>
                          ))
                        ) : (
                          <Badge variant="outline">user</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleSetAdmin(user.email)}>
                          <Shield className="h-4 w-4 mr-1" />Make Admin
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleSetTester(user.email)}>
                          <User className="h-4 w-4 mr-1" />Make Tester
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center">No users found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
